import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mail, Phone, ShieldCheck } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { LEGAL_CONTENT, LEGAL_INDEX, CONTACT_BLOCK } from "@/pages/legal/content";

export default function LegalPage() {
  const { slug } = useParams();
  const page = LEGAL_CONTENT[slug];

  useEffect(() => {
    if (page) {
      document.title = `${page.title} | APEX Innovation Solutions`;
      window.scrollTo(0, 0);
    }
    return () => {
      document.title = "APEX Innovation Solutions | Web Design, SEO & AEO Agency for Business Growth";
    };
  }, [page]);

  if (!page) return <Navigate to="/" replace />;

  return (
    <main data-testid={`legal-page-${slug}`} className="relative overflow-x-hidden">
      <Navbar />

      {/* Header */}
      <section className="relative pt-40 pb-16 md:pt-48 md:pb-20">
        <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" />
        <div className="absolute top-[-140px] left-[-160px] w-[520px] h-[520px] rounded-full bg-apex-blue/25 blur-[140px] pointer-events-none" />
        <div className="absolute top-[8%] right-[-160px] w-[460px] h-[460px] rounded-full bg-apex-purple/25 blur-[140px] pointer-events-none" />

        <div className="container-apex relative">
          <Link
            to="/"
            data-testid="legal-back-home"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>

          <div className="max-w-3xl">
            <div className="eyebrow mb-4">
              <span className="w-6 h-px bg-apex-cyan" /> Legal
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-extrabold leading-[1.05] tracking-tight">
              {page.title}
            </h1>
            <p className="mt-5 text-white/70 text-lg leading-relaxed">{page.subtitle}</p>
            <p className="mt-3 text-xs uppercase tracking-[0.28em] text-white/40">
              Last updated: {page.updated}
            </p>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="pb-24 md:pb-32 relative">
        <div className="container-apex grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Sidebar — other legal pages */}
          <aside className="lg:col-span-3">
            <div className="glass p-5 sticky top-32">
              <div className="text-xs uppercase tracking-[0.24em] text-white/50 mb-3">Legal pages</div>
              <ul className="space-y-1.5">
                {LEGAL_INDEX.map((item) => (
                  <li key={item.slug}>
                    <Link
                      to={`/legal/${item.slug}`}
                      data-testid={`legal-nav-${item.slug}`}
                      className={`block text-sm px-3 py-2 rounded-lg transition-all ${
                        item.slug === slug
                          ? "bg-apex-orange/15 border border-apex-orange/30 text-apex-orange font-semibold"
                          : "text-white/75 hover:text-white hover:bg-white/[0.04]"
                      }`}
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>

              <div className="mt-6 pt-6 border-t border-white/10 space-y-2">
                <a href={`mailto:${CONTACT_BLOCK.business_email}`} className="flex items-center gap-2 text-xs text-white/70 hover:text-white transition-colors">
                  <Mail className="w-3.5 h-3.5 text-apex-cyan" />
                  {CONTACT_BLOCK.business_email}
                </a>
                <a href={`tel:${CONTACT_BLOCK.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 text-xs text-white/70 hover:text-white transition-colors">
                  <Phone className="w-3.5 h-3.5 text-apex-orange" />
                  {CONTACT_BLOCK.phone}
                </a>
              </div>
            </div>
          </aside>

          {/* Article */}
          <article className="lg:col-span-9">
            <div className="glass p-8 md:p-12">
              <div className="flex items-center gap-3 mb-8 pb-6 border-b border-white/10">
                <span className="w-11 h-11 rounded-2xl bg-apex-cyan/10 border border-apex-cyan/25 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-apex-cyan" strokeWidth={1.75} />
                </span>
                <div>
                  <div className="text-sm text-white/60">APEX Innovation Solutions</div>
                  <div className="font-heading font-bold text-white">{page.title}</div>
                </div>
              </div>

              <div className="space-y-8">
                {page.sections.map((s, i) => (
                  <motion.div
                    key={s.heading}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.5, delay: i * 0.03, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <h2 className="font-heading font-bold text-lg md:text-xl text-white mb-2">
                      {s.heading}
                    </h2>
                    <p className="text-[15px] leading-relaxed text-white/75">{s.body}</p>
                  </motion.div>
                ))}
              </div>

              <div className="mt-10 pt-6 border-t border-white/10 text-xs text-white/50 leading-relaxed">
                By using {CONTACT_BLOCK.website}, you acknowledge that you have read and understood this document.
                Questions? Email <a href={`mailto:${CONTACT_BLOCK.business_email}`} className="text-apex-cyan hover:text-white underline underline-offset-4">{CONTACT_BLOCK.business_email}</a>.
              </div>
            </div>
          </article>
        </div>
      </section>

      <Footer />
    </main>
  );
}
